import { Injectable, signal } from '@angular/core';

import { DsmContractSelection } from '../models/dsm-contract-details.models';
import {
  DSM_SELECTION_BASKET_NO_ADJUSTMENTS,
  DsmSelectionBasket,
  DsmSelectionBasketAdjustments,
  DsmSelectionBasketSaveResult
} from '../models/dsm-selection-basket.models';

const EMPTY_BASKET: DsmSelectionBasket = { selections: [], adjustments: DSM_SELECTION_BASKET_NO_ADJUSTMENTS };

@Injectable({ providedIn: 'root' })
export class DsmSelectionBasketService {
  readonly #baskets = signal<ReadonlyMap<string, DsmSelectionBasket>>(new Map());

  basket(dealId: string | null): DsmSelectionBasket {
    return (dealId && this.#baskets().get(dealId)) || EMPTY_BASKET;
  }

  addSelections(dealId: string, selections: DsmContractSelection[]): void {
    this.#updateSelections(dealId, list => [...list, ...selections]);
  }

  replaceSelection(dealId: string, selection: DsmContractSelection): void {
    this.#updateSelections(dealId, list =>
      list.map(item => (item.id === selection.id ? { ...selection, error: null } : item))
    );
  }

  changeQuantity(dealId: string, selectionId: string, quantity: number): void {
    this.#updateSelections(dealId, list =>
      list.map(item =>
        item.id === selectionId
          ? { ...item, values: { ...item.values, quantity: Math.max(1, quantity) }, error: null }
          : item
      )
    );
  }

  removeSelection(dealId: string, selectionId: string): void {
    this.#updateSelections(dealId, list => list.filter(item => item.id !== selectionId));
  }

  setAdjustments(dealId: string, adjustments: Partial<DsmSelectionBasketAdjustments>): void {
    this.#update(dealId, basket => ({ ...basket, adjustments: { ...basket.adjustments, ...adjustments } }));
  }

  applySaveResult(dealId: string, result: DsmSelectionBasketSaveResult): void {
    const outcomes = new Map(result.options.map(option => [option.optionKey, option]));

    this.#update(dealId, basket => ({
      selections: basket.selections
        .filter(selection => !outcomes.get(selection.id)?.added)
        .map(selection => {
          const outcome = outcomes.get(selection.id);
          return outcome ? { ...selection, error: outcome.error } : selection;
        }),
      adjustments: result.addendumId ? DSM_SELECTION_BASKET_NO_ADJUSTMENTS : basket.adjustments
    }));
  }

  #updateSelections(dealId: string, change: (selections: DsmContractSelection[]) => DsmContractSelection[]): void {
    this.#update(dealId, basket => ({ ...basket, selections: change(basket.selections) }));
  }

  #update(dealId: string, change: (basket: DsmSelectionBasket) => DsmSelectionBasket): void {
    this.#baskets.update(baskets => new Map(baskets).set(dealId, change(baskets.get(dealId) ?? EMPTY_BASKET)));
  }
}
